import { useEffect, useState } from "react";
import { URL } from "../../Common/APIURL";

// Computed once when this module first loads (i.e. once per full page
// load/refresh), not per render — so swapping a static image's file on
// disk under the same filename (e.g. CommonImage.png) actually shows up
// on the next page load instead of the browser silently serving whatever
// it cached for that same URL before. Re-fetches once per session; doesn't
// spam a fresh request on every re-render.
const IMG_CACHE_BUST = Date.now();

// ─────────────────────────────────────────────
// LAB CARD
// ─────────────────────────────────────────────

const LabCard = ({ lab, isSelected, onSelect }) => {
  const [imgError, setImgError] = useState(false);

  return (
    <button
      type="button"
      onClick={() => onSelect(lab)}
      style={{
        height: "200px",
        display: "block",
        position: "relative",
        minWidth: "160px",
      }}
      className={`group w-full rounded-xl overflow-hidden cursor-pointer border-4 transition-all duration-300
        shadow-[0px_20px_40px_rgba(24,28,29,0.08)]
        ${
          isSelected
            ? "border-[#08757c]"
            : "border-transparent hover:border-[#08757c]"
        }`}
    >
      {/* Background — image or teal gradient fallback */}
      {lab.facilityImagePath && !imgError ? (
        <img
          src={`${URL}${lab.facilityImagePath}?v=${IMG_CACHE_BUST}`}
          alt={lab.facilityName}
          style={{
            position: "absolute",
            inset: 0,
            width: "100%",
            height: "100%",
            objectFit: "cover",
          }}
          className="transition-transform duration-700 group-hover:scale-105"
          onError={() => setImgError(true)}
        />
      ) : (
        <div
          style={{ position: "absolute", inset: 0 }}
          className="bg-gradient-to-br from-[#08757c] to-[#005b60]"
        />
      )}

      {/* Dark overlay */}
      <div
        style={{ position: "absolute", inset: 0 }}
        className="bg-gradient-to-t from-black/95 via-black/50 to-transparent"
      />

      {/* Selected badge */}
      {isSelected && (
        <div
          style={{ position: "absolute", top: "12px", right: "12px" }}
          className="bg-[#08757c] text-white text-[10px] font-black px-2.5 py-1 rounded-full uppercase tracking-widest flex items-center gap-1"
        >
          <span className="material-symbols-outlined text-[12px]">
            check_circle
          </span>
          Selected
        </div>
      )}

      {/* Name + temp/humidity + employee at bottom */}
      <div
        style={{ position: "absolute", bottom: 0, left: 0, right: 0 }}
        className="p-2"
      >
        <h3 className="text-xs font-extrabold text-white leading-tight truncate">
          {lab.facilityName}
        </h3>
        {lab.temperatureMin != null && (
          <div className="flex items-center gap-1 mt-0.5 flex-wrap">
            <span className="text-[9px] text-white/70 flex items-center gap-0.5">
              <span className="material-symbols-outlined text-[10px]">
                thermostat
              </span>
              {lab.temperatureMin}°–{lab.temperatureMax}°C
            </span>
            <span className="text-[9px] text-white/70 flex items-center gap-0.5">
              <span className="material-symbols-outlined text-[10px]">
                water_drop
              </span>
              {lab.humidityMin}–{lab.humidityMax}% RH
            </span>
          </div>
        )}
        {lab.empoyeeName && (
          <div className="flex items-center gap-1 mt-1">
            <span className="material-symbols-outlined text-[10px] text-white/70">
              person
            </span>
            <span className="text-[9px] text-white/80 font-semibold truncate">
              {lab.empoyeeName}
            </span>
          </div>
        )}
      </div>
    </button>
  );
};

// ─────────────────────────────────────────────
// STEP 2 — SELECT LAB
// ─────────────────────────────────────────────

export default function LabBookingStep2Lab({
  labs,
  selectedLab,
  selectedLocationName,
  onSelect,
  error,
}) {
  const [showAll, setShowAll] = useState(false);
  // Collapse back to 8 whenever the location (and so the lab list) changes
  // — otherwise picking a different location kept an "expanded" state left
  // over from a previous one that happened to have more than 8 labs.
  useEffect(() => setShowAll(false), [selectedLocationName]);

  return (
    <div className="bg-white p-4 rounded-2xl shadow-[0px_20px_40px_rgba(24,28,29,0.06)] border border-white/40">
      {error && (
        <p className="text-xs text-[#ba1a1a] mb-4 flex items-center gap-1">
          <span className="material-symbols-outlined text-[14px]">error</span>
          {error}
        </p>
      )}

      {labs.length === 0 ? (
        <p className="text-sm text-[#3e494a]">
          No labs found for this location.
        </p>
      ) : (
        <>
          {/* First 8 as cards — expands to every lab (same card style, not
              a plain-text list) once "Load More" is hit. */}
          <div
            className="grid gap-4"
            style={{
              gridTemplateColumns: "repeat(auto-fill, minmax(160px, 1fr))",
            }}
          >
            {(showAll ? labs : labs.slice(0, 8)).map((lab) => (
              <LabCard
                key={lab.facilityId}
                lab={lab}
                isSelected={selectedLab?.facilityId === lab.facilityId}
                onSelect={onSelect}
              />
            ))}
          </div>

          {labs.length > 8 && (
            <div className="flex justify-center mt-5">
              <button
                onClick={() => setShowAll((v) => !v)}
                className="flex items-center gap-1.5 px-5 py-2.5 rounded-full bg-[#e4f2f2] text-[#08757c] text-sm font-bold hover:bg-[#08757c] hover:text-white transition-colors"
              >
                {showAll ? "Show Less" : "Load More"}
                <span className="material-symbols-outlined text-[18px]">
                  {showAll ? "expand_less" : "expand_more"}
                </span>
              </button>
            </div>
          )}
        </>
      )}
    </div>
  );
}
